import { Tooltip, Ellipsis } from '@zoom/components';
import classnames from 'classnames';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { SortOrder, SortOrderType } from '../types';
import { customSort } from '../utils/sort';

export type Item = {
  id: string;
  name: string;
  subtitleLabel?: string;
  subtitleValue?: string;
};

type SortedBulkLabelsProps<T> = {
  cursorPointer?: boolean;
  items?: T[];
  label?: string;
  selectedType?: boolean;
  sortByFields?: (keyof T)[];
  sortOrder?: SortOrderType;
};

export const SortedBulkLabels = <T extends Item>({
  cursorPointer = false,
  items,
  label,
  selectedType = false,
  sortByFields = ['name'],
  sortOrder = SortOrder.ASCENDING,
}: SortedBulkLabelsProps<T>): React.ReactElement<SortedBulkLabelsProps<T>> | null => {
  const { t } = useTranslation();

  if (!items?.length) {
    return null;
  }

  if (items.length === 1) {
    const { name, subtitleValue } = items[0];
    return <>{subtitleValue ? `${name} (${subtitleValue})` : name}</>;
  }

  const itemsSorted = customSort(items, sortByFields, sortOrder);

  const sortedItemsNames = itemsSorted.map(({ id, name, subtitleLabel, subtitleValue }) => (
    <Fragment key={id}>
      {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
      <Ellipsis className="sorted-bulk-labels__tooltip-item">{name}</Ellipsis>
      {subtitleValue && (
        <span className="sorted-bulk-labels__tooltip-item-subtitle">
          {subtitleLabel}: <span className="font-weight-bold">{subtitleValue}</span>
        </span>
      )}
    </Fragment>
  ));

  const tooltipMessage = (
    <div className="sorted-bulk-labels__tooltip-container">
      <span className="sorted-bulk-labels__tooltip-title">
        {selectedType ? t('filter.selected', { count: items.length }) : label}
      </span>
      {sortedItemsNames}
    </div>
  );

  return (
    <Tooltip
      targetClassName={classnames('sorted-bulk-labels__tooltip-target', {
        'sorted-bulk-labels__tooltip-target--cursor-pointer': cursorPointer,
      })}
      message={tooltipMessage}
      placement="bottom"
    >
      {selectedType ? (
        // @ts-expect-error: Incompatible type of FC from React < v18
        <Ellipsis noTooltip>
          {itemsSorted
            .map(({ name, subtitleValue }) => (subtitleValue ? `${name} (${subtitleValue})` : name))
            .join(', ')}
        </Ellipsis>
      ) : (
        <strong>
          {items.length} {label}
        </strong>
      )}
    </Tooltip>
  );
};
